// Atlas color tokens — adaptive to light/dark theme.
// Dark = warm-ember palette, Light = paper palette.
// Both sets match the unified Fernduft design system (see --jf-* in index.css).

export interface AtlasTokens {
  bg: string;
  sidebar: string;
  surface: string;
  surface2: string;
  line: string;
  lineStrong: string;
  text: string;
  textMuted: string;
  textSoft: string;
  accent: string;
  accentInk: string;
  accent2: string;
  top: string;
  heart: string;
  base: string;
}

// The STAGE: the ground behind the graph, used when WebGL layers are composited
// over the app. It is NOT the page background (--background): in light it is
// one step darker than the paper (#eee3d2, the --jf-surface-2 value), because
// light-mode halos are source-over colour, not additive light, and need depth
// under them to read. The chrome (T.sidebar, --jf-chrome) is one step lighter
// than the stage in light and one step darker in dark (#030201, 1.07:1),
// matching the website.
// Keep in sync with --jf-stage / --graph-background in index.css and the
// pre-React loading shell in index.html.
export const ATLAS_PAGE_BACKGROUND_HEX = {
  light: '#eee3d2',
  dark: '#100c08',
} as const;

// Embed override (see src/lib/embedParams.ts). Always a validated #RRGGBB
// literal; null means "use the theme default above".
let atlasPageBackgroundOverride: string | null = null;

export function setAtlasPageBackgroundOverride(hex: string | null) {
  atlasPageBackgroundOverride = hex && /^#[0-9a-fA-F]{6}$/.test(hex) ? hex : null;
}

export function getAtlasPageBackgroundHex(isDark: boolean): string {
  if (atlasPageBackgroundOverride) return atlasPageBackgroundOverride;
  return isDark ? ATLAS_PAGE_BACKGROUND_HEX.dark : ATLAS_PAGE_BACKGROUND_HEX.light;
}

const DARK: AtlasTokens = {
  bg: '#100c08',
  sidebar: '#030201',
  surface: '#17110c',
  surface2: '#211811',
  line: '#34271a',
  lineStrong: '#4a3522',
  text: '#f4ead8',
  textMuted: '#b6a996',
  textSoft: '#8a7d68',
  accent: '#f2a33a',
  accentInk: '#f2a33a',
  accent2: '#ffc85a',
  top: '#6dc7e7',
  heart: '#cad9b4',
  base: '#c89478',
};

const LIGHT: AtlasTokens = {
  bg: ATLAS_PAGE_BACKGROUND_HEX.light,
  sidebar: '#fbf1e3',
  surface: '#fff8ed',
  surface2: '#eee3d2',
  line: '#d9c7ad',
  lineStrong: '#bfa17b',
  text: '#1a120b',
  textMuted: '#5f5142',
  textSoft: '#6f5e4b',
  accent: '#d98735',
  accentInk: '#9d4e12',
  accent2: '#9d4e12',
  top: '#3D8FB0',
  heart: '#7A9665',
  base: '#A56A4E',
};

export function getAtlasTokens(isDark: boolean): AtlasTokens {
  return isDark ? DARK : LIGHT;
}
